import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных инвестиций
 */
const ComparisonSidebar = ({ 
  savedInvestments, 
  deleteSavedInvestment, 
  loadSavedInvestment 
}) => {
  // Если нет сохраненных инвестиций
  if (!savedInvestments || savedInvestments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиций
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты инвестиций для удобного сравнения.
          Рассчитайте и сохраните несколько вариантов для сравнения их доходности.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение инвестиций
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedInvestments.length} {savedInvestments.length === 1 ? 'вариант' : savedInvestments.length < 5 ? 'варианта' : 'вариантов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedInvestments.map((investment) => (
          <div 
            key={investment.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {investment.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedInvestment(investment.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedInvestment(investment.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Начальная сумма:</span>
                <span className="font-medium">
                  {formatCurrency(investment.results.initialInvestment, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Срок:</span>
                <span>
                  {investment.results.investmentYears} {pluralize(investment.results.investmentYears, 'год', 'года', 'лет')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Ставка:</span>
                <span>
                  {formatNumber(investment.results.annualInterestRate * 100, 2)}%
                </span>
              </div>
              <div className="flex justify-between mb-1 text-green-600 dark:text-green-400">
                <span>Итоговая сумма:</span>
                <span className="font-semibold">
                  {formatCurrency(investment.results.finalBalance, 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Доходность:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(investment.results.effectiveAnnualRate * 100, 2)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {savedInvestments.length >= 2 && (
        <div className="mt-4 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2">
            Сравнительный анализ
          </h4>
          
          {/* Находим наиболее выгодный вариант */}
          {(() => {
            const mostProfitableInvestment = savedInvestments.reduce((best, current) => {
              return (current.results.effectiveAnnualRate > best.results.effectiveAnnualRate) ? current : best;
            }, savedInvestments[0]);
            
            return (
              <div className="text-xs text-indigo-600 dark:text-indigo-400">
                <p className="mb-2">
                  Наиболее выгодный вариант: <span className="font-medium">{mostProfitableInvestment.name}</span>
                </p>
                <p>Доходность: {formatNumber(mostProfitableInvestment.results.effectiveAnnualRate * 100, 2)}% годовых</p>
                <p>Итоговая сумма: {formatCurrency(mostProfitableInvestment.results.finalBalance)}</p>
                <p className="mt-1">
                  Это на {formatNumber((mostProfitableInvestment.results.finalBalance / savedInvestments.reduce((sum, inv) => sum + inv.results.finalBalance, 0) * savedInvestments.length - 1) * 100, 0)}% выше 
                  среднего показателя по всем вариантам.
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;